import React, { Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ACCESS_GRANTS_PATH } from 'accessGrants/shared';
import { withErrorBoundary } from '../../../hoc/withErrorBoundary';
import { NotFound } from '../../NotFound/NotFound';
import { Progress } from '../../Progress/Progress';

// Access grants moved into the Entitlements tabs. Redirect the old standalone
// URL, preserving the query string so saved deep links keep their filters.
const RedirectAccessGrants = () => {
  const { search } = useLocation();
  return <Navigate to={`${ACCESS_GRANTS_PATH}${search}`} replace />;
};

// Bookmarks and shared links may still point at /dspm. Redirect them to Data
// security, preserving the query string so they keep their filters.
const RedirectDataSecurity = () => {
  const { search } = useLocation();
  return <Navigate to={`/data-security${search}`} replace />;
};

// The home dashboard lives at the root, where the global filter is offered.
const RedirectHome = () => {
  const { search } = useLocation();
  return <Navigate to={`/${search}`} replace />;
};

const ComplianceInterface = withErrorBoundary(
  React.lazy(() => import('complianceInterface/ComplianceInterface')),
);

const DashboardInterface = withErrorBoundary(
  React.lazy(() => import('dashboard/dashboard')),
);

const SearchInterface = withErrorBoundary(
  React.lazy(() => import('search/search')),
);

const CWPPInterface = withErrorBoundary(
  React.lazy(() => import('workloadInterface/WorkloadSecurity')),
);

const FindingsInterface = withErrorBoundary(
  React.lazy(() => import('findings/Findings')),
);

const AlertsInterface = withErrorBoundary(
  React.lazy(() => import('alerts/Alerts')),
);

const AssetsInterface = withErrorBoundary(
  React.lazy(() => import('assets/Assets')),
);

const AgentSecurityInterface = withErrorBoundary(
  React.lazy(() => import('agentSecurity/AgentSecurity')),
);

const CIEMInterface = withErrorBoundary(React.lazy(() => import('ciem/CIEM')));

const ShiftLeftInterface = withErrorBoundary(
  React.lazy(() => import('shiftLeft/ShiftLeft')),
);

const DocsInterface = withErrorBoundary(
  React.lazy(() => import('docs/docs')),
  () => <></>,
);

const DSPMInterface = withErrorBoundary(
  React.lazy(() => import('dspm/dspm')),
  () => <></>,
);

const RiskInterface = withErrorBoundary(
  React.lazy(() => import('risk/Risk')),
  () => <></>,
);

function RoutesComponent() {
  return (
    <Suspense fallback={<Progress />}>
      <Routes>
        <Route index element={<DashboardInterface />} />
        <Route path="/home/*" element={<RedirectHome />} />
        <Route path="/pleri/*" element={<SearchInterface />} />
        <Route path="/plerion/*" element={<SearchInterface />} />
        <Route path="/search/*" element={<SearchInterface />} />
        <Route path="/favourites/*" element={<SearchInterface />} />
        <Route path="/vulnerabilities/*" element={<CWPPInterface />} />
        <Route path="/compliance/*" element={<ComplianceInterface />} />
        <Route
          path="/compliance-and-well-architected/*"
          element={<ComplianceInterface />}
        />
        <Route path="/findings/*" element={<FindingsInterface />} />
        <Route path="/well-architected/*" element={<ComplianceInterface />} />
        <Route path="/alerts/*" element={<AlertsInterface />} />
        <Route path="/assets/*" element={<AssetsInterface />} />
        <Route path="/agent-security/*" element={<AgentSecurityInterface />} />
        <Route path="/entitlements/*" element={<CIEMInterface />} />
        <Route path="/threat-mapping/*" element={<DocsInterface />} />
        <Route path="/code-security/*" element={<ShiftLeftInterface />} />
        <Route path="/data-security/*" element={<DSPMInterface />} />
        <Route path="/dspm/*" element={<RedirectDataSecurity />} />
        <Route path="/risks/*" element={<RiskInterface />} />
        <Route path="/access-grants/*" element={<RedirectAccessGrants />} />
        <Route
          path={'/resources/cloud-knowledge-base/*'}
          element={<DocsInterface />}
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export const TenantRoutes = React.memo(RoutesComponent);
